/**
 * "★ 5.0 · 86 reviews on Google" — BESIDE THE PRICE.
 *
 * dk-store has eighty-six five-star reviews and sells without them: the
 * block that holds them ("What customers say about …") sits about 1,600px
 * down a 390px-wide page, and the price is at about 450. Everything a
 * shopper decides with is above that fold, and the one piece of social
 * proof this shop has was below it.
 *
 * So the NUMBER moves up — not the reviews. One line, in the price row, and
 * a tap opens the one merged reviews view that the block's "See all" opens.
 *
 * THE WORDS ARE NOT WRITTEN HERE. `shopReviewsCountLine` already decides
 * between "{{n}} reviews on Google" and "{{n}} reviews", and it says Google
 * only when every one of them came from there — the same rule the block and
 * the shop page's trust line follow, from the same function, so the three
 * surfaces cannot disagree about whose reviews these are.
 *
 * NOTHING AT ALL AT ZERO. No skeleton, no "no reviews yet" beside a price:
 * a shop without reviews has the page it had before this existed.
 */
import React, { useState } from 'react';
import GoogleMark from './GoogleMark';
import ShopReviewsModal from './ShopReviewsModal';
import { allFromGoogle, shopReviewsCountLine } from './ShopReviewCard';
import styles from './shopReviewsBlock.module.css';

const ShopReviewsPriceChip = ({ shopReviews, salonSlug, shopName, t }) => {
  const [open, setOpen] = useState(false);

  const count = Number(shopReviews?.count) || 0;
  if (count <= 0) return null;

  const average = Number(shopReviews?.average) || 0;
  const score = average.toFixed(1);
  const google = allFromGoogle(shopReviews);
  const words = shopReviewsCountLine(shopReviews, t);
  /* The label says the rating AND that the chip goes somewhere. The visible
   * text is `aria-hidden` under it so a reader hears one sentence rather
   * than the star glyph, the digits and the words a second time. */
  const aria = google
    ? t('shopReviews.chipAria', {
      defaultValue: 'Rated {{rating}} out of 5 from {{n}} reviews on Google — see all reviews',
      rating: score, n: count, count,
    })
    : t('shopReviews.chipAriaPlain', {
      defaultValue: 'Rated {{rating}} out of 5 from {{n}} reviews — see all reviews',
      rating: score, n: count, count,
    });

  return (
    <>
      <button
        type="button"
        className={styles.priceChip}
        onClick={() => setOpen(true)}
        aria-label={aria}
        data-testid="product-detail-shop-reviews-chip"
        data-move="shop_reviews_price_chip"
        data-count={count}
      >
        <span className={styles.chipStar} aria-hidden>★</span>
        {/* The number is a number in every language. */}
        <span className={styles.chipScore} dir="ltr" aria-hidden>{score}</span>
        <span className={styles.chipDot} aria-hidden>·</span>
        <span className={styles.chipWords} aria-hidden>{words}</span>
        {google && (
          <GoogleMark className={styles.chipMark} testId="product-detail-shop-reviews-chip-google-mark" />
        )}
      </button>

      {open && (
        <ShopReviewsModal
          open
          salonSlug={salonSlug}
          shopName={shopName}
          summary={shopReviews}
          onClose={() => setOpen(false)}
          t={t}
        />
      )}
    </>
  );
};

export default ShopReviewsPriceChip;
